import React, { useState } from "react";
import CalibrationApi from "../../../../api/CalibrationApi";
import { ELightPath } from "../../../../Model/CaptureProfile";
import { FlatFieldCalibration } from "../../../../Model/ImagerCalibration";
import { getImagerCapabilities } from "../../../../redux/reducers/ImagerCapabilitiesReducer";
import { getModuleState } from "../../../../redux/reducers/ModuleStateReducer";
import { useTypedSelector } from "../../../../redux/reducers/RootReducer";
import LightPathUtil from "../../../../Utility/LightPathUtil";
import MtiButton from "../../../Buttons/MtiButton";
import SwitchButton from "../../../Buttons/SwitchButton";
import CommandPanel from "../../../CommandPanel/CommandPanel";
import TextBox from "../../../Common/TextBox";
import ImagerManualControl from "../../../Imager/ImagerManualControl";
import "./FlatFieldCorrection.css";
import Disabled from "../../../Controls/Disabled";

type Props = {
  disabled?: boolean;
  backBtn: () => void;
  imagerIdentifier: string;
  lightPath: ELightPath;
  flatFieldCalibration: FlatFieldCalibration;
  setFlatFieldCalibration: (flatFieldCalibration: FlatFieldCalibration) => void;
};

export default function FlatFieldCorrection({
  disabled,
  backBtn,
  imagerIdentifier,
  lightPath,
  flatFieldCalibration,
  setFlatFieldCalibration,
}: Props) {
  const moduleState = useTypedSelector((state) =>
    getModuleState(state, imagerIdentifier)
  );
  const imagerState = moduleState.imager;

  if (imagerState == null) {
    throw new Error("Imager state is null");
  }

  const [minimumPixelValue, setMinimumPixelValue] = useState(10);

  let binningValue: number | undefined;
  switch (lightPath) {
    case ELightPath.Visible:
      binningValue = imagerState?.captureProfile.visible?.binning;
      break;
    case ELightPath.Fluorescence:
      binningValue = imagerState?.captureProfile.fluorescence?.binning;
      break;
    case ELightPath.Ultraviolet:
      binningValue = imagerState?.captureProfile.ultraviolet?.binning;
      break;
    default:
      binningValue = undefined;
  }

  if (binningValue == null) {
    throw new Error("Binning is null");
  }

  const binningDisplay = binningValue
    ? `(${binningValue}x${binningValue} Binning)`
    : "";

  const binningFlatFieldCalibration =
    flatFieldCalibration.binningFlatFieldCalibrations.filter(
      (b) => b.binning === binningValue
    )[0];

  const badPixelRows = binningFlatFieldCalibration.indexes.map((badPixel) => {
    return (
      <div className="bad-pixel-row center-text">
        <p>{badPixel}</p>
      </div>
    );
  });

  const onClickCalibrate = () => {
    if (imagerState.activeLightPath !== lightPath) {
      alert(
        `Set the active Camera to ${LightPathUtil.toName(
          lightPath
        )} before calibrating.`
      );
      return;
    }

    CalibrationApi.getFlatFieldCalibrationOnCurrentBinning(
      imagerIdentifier,
      lightPath,
      minimumPixelValue
    ).then((binningFlatFieldCalibration) => {
      let updatedFlatFieldCalibration = { ...flatFieldCalibration };
      updatedFlatFieldCalibration.binningFlatFieldCalibrations =
        flatFieldCalibration.binningFlatFieldCalibrations.filter(
          (bfc) => bfc.binning !== binningFlatFieldCalibration.binning
        );
      updatedFlatFieldCalibration.binningFlatFieldCalibrations.push(
        binningFlatFieldCalibration
      );
      setFlatFieldCalibration(updatedFlatFieldCalibration);
    });
  };

  return (
    <CommandPanel>
      <div
        style={{
          overflow: "auto",
          WebkitBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
          MozBoxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
          boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)",
        }}
      >
        <CommandPanel.DarkBackArrowHeader
          title="Flat Field Correction"
          onBackArrowClick={backBtn}
          style={{
            height: 60,
          }}
        />

        <Disabled disabled={disabled}>
          <div className="flat-field-container">
            <div className="flat-field-header">
              <div className="center-text">
                <p>{"Bad Pixel Value " + binningDisplay}</p>
              </div>
              <div className="center-text">
                <p>Enable/Disable</p>
                <SwitchButton
                  isOn={flatFieldCalibration.enabled}
                  onToggle={(isOn) => {
                    setFlatFieldCalibration({
                      ...flatFieldCalibration,
                      enabled: isOn,
                    });
                  }}
                  withLetter={false}
                  style={{
                    marginLeft: 21,
                    display: "flex",
                    alignItems: "center",
                  }}
                />
              </div>
            </div>
            <div
              className={
                flatFieldCalibration.enabled
                  ? "bad-pixels"
                  : "bad-pixels bad-pixels-disabled"
              }
            >
              {badPixelRows}
            </div>
            <div className="flat-field-footer">
              <div className="center-text">
                <p>Minimum Pixel Value (px)</p>
              </div>
              <div>
                <TextBox
                  name="minimumPixelValue"
                  variant="number"
                  isInteger={true}
                  value={minimumPixelValue}
                  onChange={(minVal) => setMinimumPixelValue(minVal as number)}
                  disabled={!flatFieldCalibration.enabled}
                />
              </div>
              <div>
                <MtiButton
                  disabled={!flatFieldCalibration.enabled}
                  size="medium"
                  onClick={onClickCalibrate}
                  style={{ width: 135, height: 34 }}
                >
                  CALIBRATE
                </MtiButton>
              </div>
            </div>
          </div>
        </Disabled>

        <ImagerManualControl
          wellLimitEnabled={true}
          imagerIdentifier={imagerIdentifier}
          style={{
            overflow: "none",
          }}
        />
      </div>
    </CommandPanel>
  );
}
